// 급여관리 PRO — 그리드 파생 상태 계산.
// 서버(2.1 grid)는 원본값(payrollStatus / transmission / notices / manualChecks)만 내려주고,
// 6칸 납부서 판정(§5.4)을 포함한 모든 칸 상태·그룹 요약·발송 분류는 여기서 계산한다.

import {
  DerivedRow,
  EntryStatus,
  NoticeItem,
  PayeeType,
  PayrollGridRow,
  StatusCode,
  statusKind
} from "models/payrollManagement.model";
import { CycleCode, GridGroup, GROUPS, isColumnInScope } from "./grid.config";

// 이 지급월 화면에서 원천세(신고·납부서·발송)를 진행하는 달인지 (화면설명서 §5.4 규칙1).
// 반기 거래처는 상반기 마감을 6월 지급분 화면에서, 하반기 마감을 12월 지급분 화면에서 처리한다.
// (실제 신고·납부 기한은 각각 7/10·익년 1/10이지만 화면상 조치는 반기 마지막 달에 모아 둔다)
// 값이 SEMIANNUAL이 아니면(MONTHLY·미상 포함) 매월 진행으로 본다.
function isWithholdingActiveMonth(
  filingCycle: PayrollGridRow["filingCycle"],
  viewMonth: number
) {
  if (filingCycle === "SEMIANNUAL") return viewMonth === 6 || viewMonth === 12;
  return true;
}

const isPresent = (s: EntryStatus) => s !== "NOT_ENTERED";
const isClosed = (s: EntryStatus) => s === "CLOSED";

// 카톡 발송 시각 이후에 올라온 납부서·접수증이 있는지 (재발송 필요 판정)
function uploadedAfterSend(row: PayrollGridRow): boolean {
  if (!row.noticeKakaoSentAt) return false;
  const sent = new Date(row.noticeKakaoSentAt).getTime();
  return (row.notices || []).some(
    (n) => n.uploadedAt && new Date(n.uploadedAt).getTime() > sent
  );
}

function entryCell(s: EntryStatus): StatusCode {
  if (s === "CLOSED") return "ok";
  if (s === "ENTERED") return "empty";
  return "off";
}

// 납부서 칸(소득별) 판정 — §5.4의 규칙 3~5·7
// 환급(규칙 6)은 여기서 보지 않고, 접수증 금액이 0 이하이면 deriveRow에서 일괄 refund 처리한다.
function noticeCell(hasIncome: boolean, matched: NoticeItem[]): StatusCode {
  if (matched.length) {
    if (!hasIncome) return "alert"; // 7. 납부서는 있는데 대응 소득 없음
    return "att"; // 5. 첨부
  }
  if (!hasIncome) return "off"; // 3. 원래 없는 게 정상
  return "todo"; // 4. 소득 있음 + 업로드 전
}

const NOTE_KEYS = ["n_ed", "n_biz", "n_ret", "n_etc", "n_loc"] as const;

type NoteBucket =
  | "n_ed"
  | "n_biz"
  | "n_ret"
  | "n_etc"
  | "n_loc"
  | "rc_wt"
  | "etc";

// 납부서 1건 → 칸 분류. docType(카테고리) + taxItem(세목) 기준.
//   국세  · 근로소득세  → 근로/일용 (일용근로소득세 포함, "근로소득" 포함이면 매칭)
//         · 사업소득세  → 사업
//         · 퇴직소득세  → 퇴직
//         · 기타소득세  → 기타
//   지방세 · 지방소득세  → 지방세 (주민세 등 그 외 지방세는 ETC)
//   접수증               → 접수증
//   그 외(이자·배당·연금·법인세·농특세·미인식 등) → 모두 ETC
function noteBucket(n: NoticeItem): NoteBucket {
  if (n.docType === "TAX_RECEIPT") return "rc_wt";
  const item = n.taxItem || "";
  if (n.docType === "NATIONAL_TAX") {
    if (item.includes("근로소득")) return "n_ed";
    if (item.includes("사업소득")) return "n_biz";
    if (item.includes("퇴직소득")) return "n_ret";
    if (item.includes("기타소득")) return "n_etc";
  }
  if (n.docType === "LOCAL_TAX" && item.includes("지방소득")) return "n_loc";
  return "etc";
}

// 위 6칸 어디에도 해당하지 않는 납부서 (이자·배당·연금, 주민세·법인세·농특세, 미인식 등)
export function extractEtcNotices(notices: NoticeItem[]): NoticeItem[] {
  return notices.filter((n) => noteBucket(n) === "etc");
}

export interface DeriveOptions {
  viewMonth: number;
}

export function deriveRow(
  row: PayrollGridRow,
  opts: DeriveOptions
): DerivedRow {
  const { viewMonth } = opts;
  const ps = row.payrollStatus;
  const tr = row.transmission;
  const mc = row.manualChecks;

  const cells: Record<string, StatusCode> = {};

  const manual = (cell: keyof typeof mc, gate: boolean): StatusCode => {
    if (!gate) return "off";
    return mc[cell]?.done ? "filed" : "nfile";
  };
  const manualSub = (cell: keyof typeof mc, gate: boolean): StatusCode => {
    if (!gate) return "off";
    return mc[cell]?.done ? "sub" : "nsub";
  };

  // ── 급여자료 입력 ──────────────────────────────────────────────
  cells.earned = entryCell(ps.earned);
  cells.daily = entryCell(ps.daily);
  cells.biz = entryCell(ps.biz);
  cells.etc = entryCell(ps.etc);

  // 퇴직 칸 — 서버가 내려주는 retirement enum 그대로 매핑.
  cells.ret =
    row.retirement === "DONE"
      ? "ok"
      : row.retirement === "REQUIRED"
      ? "req"
      : "off";

  // ── 세무사랑 전송 (읽기 전용) ─────────────────────────────────
  // 마감 완료된 고용유형만 대상. 하나도 마감 안 됐으면 off.
  const txPairs: Array<{ status: EntryStatus; at: string | null }> = [
    { status: ps.earned, at: tr.earned },
    { status: ps.daily, at: tr.daily },
    { status: ps.biz, at: tr.biz },
    { status: ps.etc, at: tr.etc }
  ];
  const closedTypes = txPairs.filter((p) => isClosed(p.status));
  if (!closedTypes.length) cells.tx = "off";
  else cells.tx = closedTypes.every((p) => !!p.at) ? "sent" : "nsend";

  // ── 원천세 신고 (수동) ───────────────────────────────────────
  const withholdingActive = isWithholdingActiveMonth(
    row.filingCycle,
    viewMonth
  );
  const anyClosed =
    isClosed(ps.earned) ||
    isClosed(ps.daily) ||
    isClosed(ps.biz) ||
    isClosed(ps.etc);
  cells.file = manual("WITHHOLDING_TAX_FILED", withholdingActive && anyClosed);

  // ── 납부서 및 접수증 (§5.4) ─────────────────────────────────
  // 게이트: 반기 비신고월이거나 원천세 신고 ≠ filed면 전체 off
  const noteGate = withholdingActive && cells.file === "filed";
  if (!noteGate) {
    NOTE_KEYS.forEach((k) => (cells[k] = "off"));
    cells.rc_wt = "off";
  } else {
    // 납부서를 세목 기준으로 칸에 배분
    const byBucket: Record<NoteBucket, NoticeItem[]> = {
      n_ed: [],
      n_biz: [],
      n_ret: [],
      n_etc: [],
      n_loc: [],
      rc_wt: [],
      etc: []
    };
    row.notices.forEach((n) => byBucket[noteBucket(n)].push(n));

    cells.n_ed = noticeCell(
      isPresent(ps.earned) || isPresent(ps.daily),
      byBucket.n_ed
    );
    cells.n_biz = noticeCell(isPresent(ps.biz), byBucket.n_biz);
    cells.n_ret = noticeCell(cells.ret !== "off", byBucket.n_ret);
    cells.n_etc = noticeCell(isPresent(ps.etc), byBucket.n_etc);

    // 지방세: 소득 4종 중 하나라도 att/todo면 활성
    const anyIncomeNote = [
      cells.n_ed,
      cells.n_biz,
      cells.n_ret,
      cells.n_etc
    ].some((v) => v === "att" || v === "todo");
    if (!anyIncomeNote) {
      cells.n_loc = "off";
    } else {
      cells.n_loc = byBucket.n_loc.length ? "att" : "todo";
    }

    // 접수증: 신고를 했으면 있어야 정상.
    // 접수증 금액이 0 이하(환급) → 접수증 + 미첨부·첨부 상태인 납부서 칸을 전부 refund로.
    // 낼 세액이 없으니 납부서가 없는 게 정상이고, 조치 필요·발송 확인 요망에도 걸리지 않는다 (§5.4 규칙 6).
    const receipts = byBucket.rc_wt;
    const receiptRefund =
      receipts.length > 0 && receipts.every((n) => n.amount <= 0);
    cells.rc_wt = receipts.length ? (receiptRefund ? "refund" : "att") : "todo";
    if (receiptRefund) {
      NOTE_KEYS.forEach((k) => {
        if (cells[k] === "todo" || cells[k] === "att") cells[k] = "refund";
      });
    }
  }

  // ── 발송 (읽기 전용, 액션은 발송 다이얼로그) ────────────────
  // 발송 후 발송 시각보다 나중에 올라온 납부서·접수증이 있으면 redo(재발송 필요, §5.5)
  const needSlip = NOTE_KEYS.some((k) => cells[k] !== "off");
  if (!needSlip) cells.dispatch = "off";
  else if (row.noticeKakaoSentAt)
    cells.dispatch = uploadedAfterSend(row) ? "redo" : "done";
  else cells.dispatch = "nmail";

  // ── 공단업무 ────────────────────────────────────────────────
  cells.i_day = manual("INSURANCE_DAILY_WORK_REPORT", isClosed(ps.daily));
  // 취득 = 신규입사자의 최초 지급월, 상실 = 퇴사자의 퇴사월. 서버가 귀속월 기준으로
  // 판정해 내려주는 플래그(insuranceAcquisitionDue/insuranceLossDue)로 칸을 띄운다.
  // 이미 담당자가 체크한 칸은 플래그가 꺼져도 계속 노출한다.
  cells.i_acq = manual(
    "INSURANCE_ACQUISITION",
    row.insuranceAcquisitionDue || !!mc.INSURANCE_ACQUISITION
  );
  cells.i_loss = manual(
    "INSURANCE_LOSS",
    row.insuranceLossDue || !!mc.INSURANCE_LOSS
  );

  // ── 매월 지급명세서 ────────────────────────────────────────
  cells.s_day = manualSub("MONTHLY_STATEMENT_DAILY", isPresent(ps.daily));
  cells.q_biz = manualSub(
    "MONTHLY_STATEMENT_BUSINESS_SIMPLE",
    isPresent(ps.biz)
  );
  cells.q_etc = manualSub("MONTHLY_STATEMENT_OTHER_SIMPLE", isPresent(ps.etc));

  // ── 연간 지급명세서 ────────────────────────────────────────
  cells.q_ear = manualSub(
    "ANNUAL_STATEMENT_EARNED_SIMPLE",
    isPresent(ps.earned)
  );
  cells.s_ear = manualSub("ANNUAL_STATEMENT_EARNED", isPresent(ps.earned));
  cells.s_biz = manualSub("ANNUAL_STATEMENT_BUSINESS", isPresent(ps.biz));
  cells.s_ret = manualSub("ANNUAL_STATEMENT_RETIREMENT", cells.ret !== "off");
  cells.s_etc = manualSub("ANNUAL_STATEMENT_OTHER", isPresent(ps.etc));

  return {
    ...row,
    cells,
    dispatched: !!row.noticeKakaoSentAt,
    basisLabel: row.reportBasis === "PREV_MONTH" ? "전월" : "당월",
    typeLabel: row.filingCycle === "SEMIANNUAL" ? "반기" : "월별"
  };
}

// ---------------------------------------------------------------------------
// 그룹 요약 / 완료 판정 / 발송 분류
// ---------------------------------------------------------------------------

// 0 = 모두 완료, 1 = 미완료 있음, 2 = 해당없음
export function groupState(
  row: DerivedRow,
  group: GridGroup,
  viewMonth: number
): 0 | 1 | 2 {
  const vals = group.cols
    .filter((c) => c.id !== "etc_notice")
    .filter((c) => isColumnInScope(c.cycle as CycleCode | undefined, viewMonth))
    .map((c) => row.cells[c.id])
    .filter((v) => v && v !== "off");
  if (!vals.length) return 2;
  return vals.every((v) => statusKind(v) === "done") ? 0 : 1;
}

// 접힌 "납부서 및 접수증" 그룹에 그릴 대표 상태 코드.
// 전부 환급이면 refund(초록 빈네모), 그 외 완료면 att(초록 채움)로 구분한다.
export function noteGroupCode(row: DerivedRow): StatusCode {
  const noteGroup = GROUPS.find((g) => g.id === "note");
  const vals = (noteGroup?.cols ?? [])
    .filter((c) => c.id !== "etc_notice")
    .map((c) => row.cells[c.id])
    .filter((v): v is StatusCode => !!v && v !== "off");
  if (!vals.length) return "off";
  if (vals.some((v) => statusKind(v) === "alert")) return "alert";
  if (vals.every((v) => v === "refund")) return "refund";
  if (vals.every((v) => statusKind(v) === "done")) return "att";
  return "todo";
}

export function openMemos(row: DerivedRow) {
  return (row.memos || []).filter((m) => !m.done);
}

export function rowHasAlert(row: DerivedRow): boolean {
  return Object.values(row.cells).some((v) => statusKind(v) === "alert");
}

export function rowIsDone(row: DerivedRow, viewMonth: number): boolean {
  return (
    !GROUPS.some((g) => groupState(row, g, viewMonth) === 1) &&
    openMemos(row).length === 0
  );
}

// ---------------------------------------------------------------------------
// 납부서 알림톡 발송 모드 상단 집계 분류
// ---------------------------------------------------------------------------
// sent            : 카톡 발송 완료
// unsent-ready    : 미발송 · 납부서·접수증이 전부 초록(첨부/환급)
// unsent-check    : 미발송 · 확인 요망 (빨간 느낌표 있거나 일부만 첨부)
// unsent-noupload : 미발송 · 납부서·접수증이 하나도 안 올라옴
// none            : 발송 대상 아님 (납부서 칸 전체 off)
export type SendClass =
  | "sent"
  | "unsent-ready"
  | "unsent-check"
  | "unsent-noupload"
  | "none";

const SEND_NOTE_KEYS = [...NOTE_KEYS, "rc_wt"];

// 재발송 필요 = 발송은 했으나 그 뒤에 납부서·접수증이 갱신됨 (deriveRow가 dispatch=redo 로 표시)
export const isResendNeeded = (row: DerivedRow) =>
  row.cells.dispatch === "redo";

export function sendClass(row: DerivedRow): SendClass {
  if (row.dispatched) return "sent";
  if (row.cells.dispatch === "off") return "none";
  const active = SEND_NOTE_KEYS.map((k) => row.cells[k]).filter(
    (v) => v && v !== "off"
  );
  const anyGreen = active.some((v) => v === "att" || v === "refund");
  const anyAlert = active.some((v) => v === "alert");
  const anyTodo = active.some((v) => v === "todo");
  if (!anyGreen && !anyAlert) return "unsent-noupload";
  if (anyAlert || anyTodo) return "unsent-check";
  return "unsent-ready";
}

// ---------------------------------------------------------------------------
// 급여 세무사랑 전송 모드 상단 집계 분류
// ---------------------------------------------------------------------------
// sent             : 급여자료 입력이 전부 마감 완료됐고, 마감된 고용유형이 전부 전송 완료
// unsent-ready     : 급여자료 입력이 전부 마감 완료됐지만 아직 전송 전 (입력완료, 초록 네모)
// unsent-inprogress: 아직 입력 중인 고용유형이 있어 전송할 수 없음 (입력중, 노란 빈네모)
// none             : 이 지급월에 해당 사업자의 급여자료 자체가 없음 — 집계·전송 대상 아님
export type TxClass = "sent" | "unsent-ready" | "unsent-inprogress" | "none";

const PAYEE_KEYS = ["earned", "daily", "biz", "etc"] as const;
type PayeeKey = (typeof PAYEE_KEYS)[number];

const PAYEE_TYPE_CODE: Record<PayeeKey, PayeeType> = {
  earned: "EMPLOYEE",
  daily: "DAILY_WORKER",
  biz: "BUSINESS_INCOME",
  etc: "OTHER_INCOME"
};

const PAYEE_TYPE_LABEL: Record<PayeeKey, string> = {
  earned: "근로",
  daily: "일용",
  biz: "사업",
  etc: "기타"
};

export function txClass(row: DerivedRow): TxClass {
  const ps = row.payrollStatus;
  const present = PAYEE_KEYS.map((k) => ps[k]).filter(
    (s) => s !== "NOT_ENTERED"
  );
  if (!present.length) return "none";
  if (!present.every((s) => s === "CLOSED")) return "unsent-inprogress";
  return row.cells.tx === "sent" ? "sent" : "unsent-ready";
}

// 전송 대상 — 마감 완료된 고용유형 전체(이미 전송된 것도 포함해 재전송 가능하게 한다).
export function txSendTargets(
  row: PayrollGridRow
): { payeeType: PayeeType; label: string }[] {
  const ps = row.payrollStatus;
  return PAYEE_KEYS.filter((k) => ps[k] === "CLOSED").map((k) => ({
    payeeType: PAYEE_TYPE_CODE[k],
    label: PAYEE_TYPE_LABEL[k]
  }));
}

// §2.9 세무사랑 전송 API의 yearMonth(귀속연월, yyyy-MM)는 화면 지급월(period)과 다를 수 있다 (BR-03).
export function accrualPeriod(
  period: string,
  reportBasis: PayrollGridRow["reportBasis"]
): string {
  if (reportBasis !== "PREV_MONTH") return period;
  const [y, m] = period.split("-").map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
